import React from 'react';
import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import { BuildingOffice2Icon, CheckBadgeIcon, SparklesIcon } from '@heroicons/react/24/outline';

import { BadgeButton } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import { messageFromError, messageOfFailure } from '@oysterjs/core/graphql/errors';

import {
  ClientField,
  FieldSource,
  type ClientFieldProvenanceFieldsFragment,
  useConfirmClientFieldMutation
} from '~/api/graphql';
import { dateFormatter } from '~/components/table-formatters';
import { FormError } from '~/components/form-error';
import { integrationName } from '~/lib/integrations';

// The popover chrome the app's other panels use; use-case-browser.tsx is the
// same set.
const PANEL_CLASS = clsx(
  'z-20 w-72 rounded-xl border border-neutral-200 bg-white p-4 shadow-lg ring-1 ring-black/5',
  'focus:outline-none dark:border-neutral-700 dark:bg-neutral-800 dark:ring-white/10',
  'origin-top transition data-[closed]:scale-95 data-[closed]:opacity-0',
  'data-[enter]:duration-100 data-[enter]:ease-out data-[leave]:duration-75 data-[leave]:ease-in'
);

const CONFIRM_FAILED = 'Could not confirm this value. Please try again.';

// Amber for Cara's own: it is the one tag that says "nobody has checked this",
// and the brief asks for unverified values to be visibly labeled.
const SOURCE_STYLE: Record<
  FieldSource,
  { color: 'sky' | 'amber' | 'green'; icon: typeof BuildingOffice2Icon }
> = {
  [FieldSource.AmsSourced]: { color: 'sky', icon: BuildingOffice2Icon },
  [FieldSource.CaraGenerated]: { color: 'amber', icon: SparklesIcon },
  [FieldSource.HumanConfirmed]: { color: 'green', icon: CheckBadgeIcon }
};

/** Indexes a client's tags by field, for the profile block to look each one up. */
export const provenanceByField = (
  tags: readonly ClientFieldProvenanceFieldsFragment[]
): Partial<Record<ClientField, ClientFieldProvenanceFieldsFragment>> =>
  Object.fromEntries(tags.map((tag) => [tag.field, tag]));

// Each label names the source, so they read as one set: a system of record, or
// Cara, or a person. "AMS" is only the fallback -- sourceLabel names the AMS
// itself whenever the tag carries which one.
const SOURCE_LABEL: Record<FieldSource, string> = {
  [FieldSource.AmsSourced]: 'AMS',
  [FieldSource.CaraGenerated]: 'Cara',
  [FieldSource.HumanConfirmed]: 'Human'
};

const sourceLabel = (tag: ClientFieldProvenanceFieldsFragment): string =>
  tag.source === FieldSource.AmsSourced
    ? (tag.integrationType && integrationName(tag.integrationType)) || SOURCE_LABEL[tag.source]
    : SOURCE_LABEL[tag.source];

/**
 * Whether an AMS's "last changed by" is worth showing. Each AMS puts what it
 * likes in that column, and an employee code or a numeric key reads as noise
 * beside a date. A login or a name is worth a line; "10442" is not.
 */
const namesAPerson = (updatedBy: string | null | undefined): boolean =>
  !!updatedBy && /[a-z]/i.test(updatedBy);

/**
 * Who filled the value and when. Doubles as the trigger's title and aria-label,
 * so a hover answers without opening anything.
 */
export const provenanceSummary = (tag: ClientFieldProvenanceFieldsFragment): string => {
  const on = dateFormatter.format(new Date(tag.recordedAt));
  if (tag.source === FieldSource.HumanConfirmed) {
    const who = tag.confirmedBy?.name || tag.confirmedBy?.email || 'a teammate';
    return `Confirmed by ${who} on ${on}`;
  }
  if (tag.source === FieldSource.CaraGenerated) {
    return `Filled by Cara on ${on}, and not confirmed`;
  }
  const ams = tag.integrationType ? integrationName(tag.integrationType) : 'your AMS';
  return namesAPerson(tag.sourceUpdatedBy)
    ? `From ${ams}, last changed by ${tag.sourceUpdatedBy} on ${on}`
    : `From ${ams}, as of ${on}`;
};

/**
 * Where a field's value came from, beside the field's label. Hovering answers
 * the question; clicking opens the confirmation a person can give the value.
 */
export const FieldSourceTag = (props: {
  clientId: string;
  tag: ClientFieldProvenanceFieldsFragment;
}) => {
  const { tag } = props;
  const [confirmField, { loading }] = useConfirmClientFieldMutation();
  const [error, setError] = React.useState<string | null>(null);

  const summary = provenanceSummary(tag);
  const { color, icon: Icon } = SOURCE_STYLE[tag.source];

  const confirm = async (close: () => void) => {
    setError(null);
    try {
      // Refetches rather than patching the cached list: the server derives the
      // AMS entries, so the tag this replaces may be no row at all.
      const result = await confirmField({
        // tag.value is what this chip was drawn from, so a save by someone else
        // since the page loaded is refused rather than credited to this user.
        variables: { clientId: props.clientId, field: tag.field, value: tag.value },
        refetchQueries: ['GetClientOverview'],
        awaitRefetchQueries: true
      });
      const message = messageOfFailure(result, CONFIRM_FAILED);
      if (message) {
        setError(message);
        return;
      }
      close();
    } catch (err) {
      // errorPolicy resolves a GraphQL error, but a dead link or a failed
      // awaited refetch still rejects, and the popover would say nothing.
      setError(messageFromError(err, CONFIRM_FAILED));
    }
  };

  return (
    <Headless.Popover className="relative inline-block align-middle">
      <Headless.PopoverButton
        as={BadgeButton}
        color={color}
        type="button"
        title={summary}
        aria-label={summary}
      >
        <Icon aria-hidden="true" className="size-3" />
        {sourceLabel(tag)}
      </Headless.PopoverButton>

      <Headless.PopoverPanel
        transition
        anchor={{ to: 'bottom start', gap: 8 }}
        className={PANEL_CLASS}
      >
        {({ close }) => (
          <>
            <p className="m-0 text-sm text-neutral-900 dark:text-neutral-100">{summary}</p>

            <FormError message={error} className="mt-2 text-xs text-red-600 dark:text-red-400" />

            {tag.source !== FieldSource.HumanConfirmed && (
              <Button
                // Not outline: Button spins a white spinner for every color but
                // white, which over this panel would be invisible in light mode.
                color="white"
                className="mt-4 w-full"
                loading={loading}
                onClick={() => void confirm(close)}
              >
                Confirm this value
              </Button>
            )}
          </>
        )}
      </Headless.PopoverPanel>
    </Headless.Popover>
  );
};
